import type { AdminBugReport, Paginated } from "@lucky-try/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog } from "../components/ui/dialog";
import { Field, Input, Select, Textarea } from "../components/ui/form";
import {
  Badge,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Pagination,
  Toolbar,
  type Tone,
} from "../components/ui/misc";
import { api, errorMessage } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useCan } from "../lib/session";

const tones: Record<AdminBugReport["status"], Tone> = {
  new: "warning",
  triaged: "info",
  resolved: "success",
  dismissed: "neutral",
};

export function ReportsPage() {
  const search = useSearch({ from: "/authed/reports" });
  const navigate = useNavigate({ from: "/reports" });
  const queryClient = useQueryClient();
  const canWrite = useCan("feedback.write");
  const [term, setTerm] = useState(search.q ?? "");
  const [open, setOpen] = useState<AdminBugReport | null>(null);

  const reports = useQuery({
    queryKey: ["reports", search],
    queryFn: () =>
      api<Paginated<AdminBugReport>>("/admin/reports", { query: { ...search, pageSize: 25 } }),
  });

  const update = useMutation({
    mutationFn: (report: AdminBugReport) =>
      api<void>(`/admin/reports/${report.id}`, {
        method: "PATCH",
        body: { status: report.status, adminNote: report.adminNote },
      }),
    onSuccess: async () => {
      setOpen(null);
      await queryClient.invalidateQueries({ queryKey: ["reports"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Report updated");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (reports.error)
    return <ErrorState error={reports.error} onRetry={() => void reports.refetch()} />;

  return (
    <>
      <PageHeader title="Bug reports" description="Player-submitted problems, newest first." />

      <Toolbar>
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({
              search: (previous) => ({ ...previous, page: 1, q: term || undefined }),
            });
          }}
        >
          <Input
            placeholder="Search text, name or email"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            className="w-64"
          />
          <Button type="submit">Search</Button>
        </form>
        <Select
          value={search.status ?? ""}
          aria-label="Status filter"
          onChange={(event) =>
            void navigate({
              search: (previous) => ({
                ...previous,
                page: 1,
                status: (event.target.value || undefined) as typeof search.status,
              }),
            })
          }
        >
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="triaged">Triaged</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
        </Select>
      </Toolbar>

      <div className="border-line bg-surface rounded-card overflow-hidden border">
        {reports.isPending ? (
          <Loading />
        ) : reports.data.items.length === 0 ? (
          <EmptyState>Nothing reported.</EmptyState>
        ) : (
          <ul>
            {reports.data.items.map((report) => (
              <li key={report.id} className="t-row border-line/60 border-b last:border-0">
                <button
                  type="button"
                  onClick={() => setOpen(report)}
                  className="w-full px-4 py-3 text-left"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={tones[report.status]}>{report.status}</Badge>
                    <span className="text-ink text-ui font-medium">
                      {report.game?.name ?? "General"}
                    </span>
                    <span className="text-muted text-caption">
                      {report.name} · {report.email}
                    </span>
                    <span className="text-muted text-caption ml-auto">
                      {formatDateTime(report.createdAt)}
                    </span>
                  </div>
                  <p className="text-body text-caption mt-1 line-clamp-2">{report.description}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
        <Pagination
          page={reports.data?.page ?? 1}
          pageSize={reports.data?.pageSize ?? 25}
          total={reports.data?.total ?? 0}
          onPage={(page) => void navigate({ search: (previous) => ({ ...previous, page }) })}
        />
      </div>

      <Dialog
        open={open !== null}
        onClose={() => setOpen(null)}
        width="lg"
        eyebrow="Bug report"
        title={open?.game?.name ?? "General"}
        footer={
          canWrite && (
            <Button
              variant="primary"
              disabled={update.isPending}
              onClick={() => open && update.mutate(open)}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          )
        }
      >
        {open && (
          <div className="text-caption flex flex-col gap-3">
            <p className="border-line bg-raised text-body text-ui rounded border p-3 whitespace-pre-wrap">
              {open.description}
            </p>
            <dl className="text-muted grid grid-cols-[110px_1fr] gap-y-1">
              <dt>Reporter</dt>
              <dd className="text-body">
                {open.name} · {open.email}
              </dd>
              <dt>Game</dt>
              <dd className="text-body">
                {open.game ? (
                  <Link
                    to="/games/$gameId"
                    params={{ gameId: String(open.game.id) }}
                    className="text-accent hover:underline"
                  >
                    {open.game.name}
                  </Link>
                ) : (
                  "—"
                )}
              </dd>
              <dt>Page</dt>
              <dd className="text-body break-all">{open.pageUrl ?? "—"}</dd>
              <dt>Country</dt>
              <dd className="text-body">{open.country ?? "—"}</dd>
              <dt>User agent</dt>
              <dd className="text-body break-all">{open.userAgent ?? "—"}</dd>
              <dt>Received</dt>
              <dd className="text-body">{formatDateTime(open.createdAt)}</dd>
            </dl>
            <Field label="Status">
              <Select
                value={open.status}
                disabled={!canWrite}
                onChange={(event) =>
                  setOpen({ ...open, status: event.target.value as AdminBugReport["status"] })
                }
              >
                <option value="new">New</option>
                <option value="triaged">Triaged</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </Select>
            </Field>
            <Field label="Internal note">
              <Textarea
                rows={3}
                disabled={!canWrite}
                value={open.adminNote ?? ""}
                onChange={(event) => setOpen({ ...open, adminNote: event.target.value || null })}
              />
            </Field>
          </div>
        )}
      </Dialog>
    </>
  );
}
